import React, { useEffect } from 'react';
import { firebaseConfig } from '@/lib/firebase';
import { DEV_TITLE_PREFIX, isDevProject } from '@/utils/environment';

/**
 * Persistent, non-blocking "DEV" indicator shown when the app is pointed at
 * a non-prod Firebase project. Driven by `firebaseConfig.projectId` — the
 * same value that decides which backend the app talks to — so it cannot
 * drift from the environment. Renders nothing when the projectId is
 * `gamut-prod`; anything else (including unknown) reads as dev.
 *
 * Mirrors the iOS dev build's amber "DEV" icon band. Mounted at the top of
 * `App` so no route escapes it; `pointer-events-none` keeps it from ever
 * blocking a click.
 */
/**
 * WEB-111: `inline` renders the same badge as a chip inside the top bar; while an inline badge is on the page the fixed
 * one hides (so the badge never sits over the bar's tabs), and routes without the shell (sign-in) keep the fixed one.
 */
export const EnvironmentBadge: React.FC<{ inline?: boolean }> = ({ inline = false }) => {
  const isDev = isDevProject(firebaseConfig.projectId);

  useEffect(() => {
    if (isDev && !document.title.startsWith(DEV_TITLE_PREFIX)) {
      document.title = `${DEV_TITLE_PREFIX}${document.title}`;
    }
  }, [isDev]);

  if (!isDev) return null;

  if (inline) {
    return (
      <span
        aria-label="Development environment"
        data-env-badge-inline=""
        className="shrink-0 rounded-md bg-[#FFB300] px-2 py-0.5 text-xs font-bold tracking-widest text-black select-none"
      >
        DEV
      </span>
    );
  }

  return (
    <div
      aria-label="Development environment"
      className="[body:has([data-env-badge-inline])_&]:hidden fixed top-0 left-1/2 z-[9999] -translate-x-1/2 rounded-b-md bg-[#FFB300] px-3 py-0.5 text-xs font-bold tracking-widest text-black shadow-lg pointer-events-none select-none"
    >
      DEV
    </div>
  );
};
